import { Activity, Item, Status } from './types';
import { statusKind } from '@/components/status-dot';

/** Pull a status value out of an activity's old/new payload: a `moved` activity stores the raw status
 *  VALUE string; a `created`/`updated` activity stores a full item snapshot object (with `.status`). */
function statusOf(v: unknown): string | undefined {
  if (typeof v === 'string') return v || undefined;
  if (v && typeof v === 'object') {
    const s = (v as { status?: unknown }).status;
    return typeof s === 'string' && s ? s : undefined;
  }
  return undefined;
}

export interface StatusSegment {
  value: string;
  days: number;
}

export interface StatusTimeline {
  /** Total days spent in each status, in order of first entry. */
  perStatus: StatusSegment[];
  /** The status the item is in now, and how long it has been there (days). */
  currentValue: string;
  currentDays: number;
  /** Days to completion: first in-progress → first done (cycle time), or — for an item completed
   *  without ever being in-progress — creation → first done (lead time). Null until the item is done. */
  timeToCompleteDays: number | null;
  /** Which span `timeToCompleteDays` measured: 'in_progress' (cycle time) or 'created' (lead-time
   *  fallback for items checked off straight from a to-do status). Null while the item isn't done. */
  timeToCompleteFrom: 'in_progress' | 'created' | null;
  /** Total days since the item was created. */
  ageDays: number;
}

const MS_DAY = 86_400_000;

/**
 * Reconstruct an item's status history from its activity trail: how long it has spent in each status
 * and the "time to complete" (in-progress → complete). Status changes come from `moved` activities
 * (raw value) and `updated` activities whose snapshot status differs; the timeline starts at the
 * item's creation in the status recorded by the `created` activity (or the earliest known status).
 */
export function buildStatusTimeline(activity: Activity[], item: Pick<Item, 'created_at' | 'status'>, statuses: Status[], nowMs: number = Date.now()): StatusTimeline {
  const labelOf = new Map(statuses.map((s) => [s.value, s.label]));
  const kindOf = (value: string) => statusKind(labelOf.get(value) ?? value);
  const createdMs = item.created_at ? +new Date(item.created_at) : nowMs;
  const acts = [...activity].sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));

  // The status the item started in: from the create snapshot, else the first change's "from", else current.
  let initial: string | undefined;
  const created = acts.find((a) => a.kind === 'created');
  if (created) initial = statusOf(created.new);
  if (!initial) {
    const firstChange = acts.find((a) => a.kind === 'moved' || (a.kind === 'updated' && statusOf(a.new) !== undefined && statusOf(a.new) !== statusOf(a.old)));
    if (firstChange) initial = statusOf(firstChange.old);
  }
  if (!initial) initial = item.status;

  // Ordered (value, enteredAt) transitions, collapsing consecutive same-status entries.
  const transitions: { value: string; atMs: number }[] = [{ value: initial, atMs: createdMs }];
  for (const a of acts) {
    let to: string | undefined;
    if (a.kind === 'moved') to = statusOf(a.new);
    else if (a.kind === 'updated') {
      const n = statusOf(a.new);
      if (n && n !== statusOf(a.old)) to = n;
    }
    if (to && to !== transitions[transitions.length - 1].value) transitions.push({ value: to, atMs: +new Date(a.created_at) });
  }

  // Sum durations per status (a status re-entered later accumulates), keeping first-seen order.
  const dur = new Map<string, number>();
  const order: string[] = [];
  for (let i = 0; i < transitions.length; i++) {
    const start = transitions[i].atMs;
    const end = i + 1 < transitions.length ? transitions[i + 1].atMs : nowMs;
    const v = transitions[i].value;
    if (!dur.has(v)) order.push(v);
    dur.set(v, (dur.get(v) ?? 0) + Math.max(0, end - start));
  }

  const last = transitions[transitions.length - 1];
  const currentIsDone = kindOf(last.value) === 'done';
  // Time to complete: from when work started to when the item LAST reached a completed status — an item
  // that bounced complete↔in-progress is measured to its FINAL completion, not the first (so a history
  // that merely begins already-complete can't collapse the span to zero). "Work started" is the first
  // in-progress entry; with no in-progress step at all (checked straight off a to-do status) fall back
  // to creation → last done (a lead time). Only meaningful once the item is actually complete now, so it
  // stays "—" otherwise — and an item BORN done (no elapsed time) stays "—" too.
  let timeToCompleteDays: number | null = null;
  let timeToCompleteFrom: 'in_progress' | 'created' | null = null;
  if (currentIsDone) {
    let firstProgressAt: number | null = null;
    let lastDoneAt: number | null = null;
    for (const t of transitions) {
      const k = kindOf(t.value);
      if (k === 'progress' && firstProgressAt === null) firstProgressAt = t.atMs;
      if (k === 'done') lastDoneAt = t.atMs; // keep overwriting → the LAST completion
    }
    if (lastDoneAt !== null && firstProgressAt !== null && firstProgressAt <= lastDoneAt) {
      timeToCompleteDays = Math.max(0, lastDoneAt - firstProgressAt) / MS_DAY;
      timeToCompleteFrom = 'in_progress';
    } else if (lastDoneAt !== null && lastDoneAt > createdMs) {
      timeToCompleteDays = Math.max(0, lastDoneAt - createdMs) / MS_DAY;
      timeToCompleteFrom = 'created';
    }
  }

  return {
    perStatus: order.map((value) => ({ value, days: (dur.get(value) ?? 0) / MS_DAY })),
    currentValue: last.value,
    currentDays: Math.max(0, nowMs - last.atMs) / MS_DAY,
    timeToCompleteDays,
    timeToCompleteFrom,
    ageDays: Math.max(0, nowMs - createdMs) / MS_DAY,
  };
}

/** Format a DURATION given in days for display: "3.4d", "18h", "12m", "5s". Always a span of time
 *  spent (never "just now" — these are elapsed durations, not recency). Rounds within a unit but
 *  carries at the boundary, so 23.99h reads "1.0d" not "24h" (likewise 60m→"1h", 60s→"1m"). */
export function formatDays(days: number): string {
  const d = Math.max(0, days);
  if (d >= 1) return `${d.toFixed(d < 10 ? 1 : 0)}d`;
  const hours = d * 24;
  if (hours >= 1) {
    const h = Math.round(hours);
    return h >= 24 ? '1.0d' : `${h}h`;
  }
  const mins = hours * 60;
  if (mins >= 1) {
    const m = Math.round(mins);
    return m >= 60 ? '1h' : `${m}m`;
  }
  const s = Math.round(mins * 60);
  return s >= 60 ? '1m' : `${s}s`;
}
